<template>
  <NavBar pageName="User Preferences"></NavBar>
  <v-container style="margin-top: 185px;">
    <v-btn variant="text" size="small" @click="$router.push('/settings')" class="mb-2">
      <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
      Settings
    </v-btn>
    <v-row>
      <v-col cols="12" md="8" lg="6">
        <v-card>
          <v-card-title>
            <v-icon class="mr-2">mdi-cog-outline</v-icon>
            User Preferences
          </v-card-title>
          <v-card-text>
            <v-form>
              <!-- Notifications Enable/Disable -->
              <div class="mb-4">
                <div class="text-subtitle-1 mb-2">Notifications</div>
                <v-switch
                  v-model="notificationsEnabled"
                  :label="notificationsEnabled ? 'Notifications ON' : 'Notifications OFF'"
                  color="primary"
                  hide-details
                  class="mb-3"
                ></v-switch>
                <div class="text-caption text-grey">
                  {{ notificationsEnabled ? 'The app will periodically check for new notifications.' : 'Notification polling is disabled. No API calls will be made.' }}
                </div>
              </div>

              <!-- Notification Polling Interval (only show if enabled) -->
              <div v-if="notificationsEnabled" class="mb-4">
                <div class="text-subtitle-1 mb-2">Polling Interval</div>
                <div class="text-caption text-grey mb-3">
                  How often the app checks for new notifications.
                </div>
                <v-select
                  v-model="notificationInterval"
                  :items="intervalOptions"
                  item-title="label"
                  item-value="value"
                  label="Polling Interval"
                  variant="outlined"
                  density="comfortable"
                ></v-select>
              </div>

              <v-divider class="my-4"></v-divider>

              <!-- Save Button -->
              <v-btn
                color="primary"
                @click="savePreferences"
                :loading="saving"
              >
                <v-icon start>mdi-content-save</v-icon>
                Save Preferences
              </v-btn>
            </v-form>
          </v-card-text>
        </v-card>

        <!-- Snackbar -->
        <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="3000">
          {{ snackbarMessage }}
        </v-snackbar>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import { log } from "@/composables/useLog";

export default defineComponent({
  name: 'UserPreferencesSettings',
  components: {
    NavBar
  },
  data() {
    return {
      notificationsEnabled: true,
      notificationInterval: 300000, // Default 5 minutes
      intervalOptions: [
        { label: '30 seconds', value: 30000 },
        { label: '1 minute', value: 60000 },
        { label: '2 minutes', value: 120000 },
        { label: '5 minutes', value: 300000 },
        { label: '10 minutes', value: 600000 },
        { label: '15 minutes', value: 900000 },
        { label: '30 minutes', value: 1800000 },
      ],
      saving: false,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success'
    }
  },
  methods: {
    loadPreferences() {
      const savedInterval = localStorage.getItem('notificationPollingInterval');
      if (savedInterval !== null) {
        const interval = parseInt(savedInterval, 10);
        if (interval === 0) {
          this.notificationsEnabled = false;
          this.notificationInterval = 300000; // Default when re-enabled
        } else {
          this.notificationsEnabled = true;
          this.notificationInterval = interval;
        }
      }
      log("Loaded notifications enabled: " + this.notificationsEnabled + ", interval: " + this.notificationInterval);
    },
    savePreferences() {
      this.saving = true;
      try {
        // If notifications disabled, save 0; otherwise save the selected interval
        const intervalToSave = this.notificationsEnabled ? this.notificationInterval : 0;
        localStorage.setItem('notificationPollingInterval', intervalToSave.toString());

        // Dispatch event to notify NotificationsPanel of the change
        window.dispatchEvent(new CustomEvent('preferences-updated', {
          detail: { notificationPollingInterval: intervalToSave }
        }));

        this.snackbarColor = 'success';
        this.snackbarMessage = 'Preferences saved successfully';
        this.showSnackbar = true;
        log("Saved notification interval: " + intervalToSave + " (enabled: " + this.notificationsEnabled + ")");
      } catch (e) {
        this.snackbarColor = 'error';
        this.snackbarMessage = 'Error saving preferences';
        this.showSnackbar = true;
        log("Error saving preferences: " + e.message);
      } finally {
        this.saving = false;
      }
    }
  },
  created() {
    this.loadPreferences();
  }
});
</script>
